/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-console */
/**
@format
@flow local-strict
*/
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { eventNames } from '@ziynx/analytics';
import Colors from '@ziynx/theme/colors';
import Card from '@ziynx/components/cards';
import Link from '@ziynx/components/links';
import ListItem from '@ziynx/components/listItems/';
import { Heading } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import { withAppManager, useNotification, LoadingSpinner } from '@ziynx/components/utilities';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import ActionTypes from '@ziynx/action-types';

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: Object,

  /**
   * Location represents where
   * the app is at right now
   */
  location: Object,
|}>;

const _getCardPadding = (sibling, isMobile) => {
  if (!isMobile) return `padding-${sibling}: 20px;`;
  if (sibling === 'left') return 'padding-top: 40px;';
};

const PageWrapper = styled.div`
  display: flex;
  max-width: 688px;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobile }) => (isMobile ? '40px 16px' : '40px')};
  ${({ isMobile }) => isMobile && 'margin: auto;'}
`;

const PageContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
`;

const StyledHeadingWrapper = styled.div`
  padding-bottom: ${({ small }) => (small ? 24 : 32)}px;
  width: max-content;
`;

const CardWrapper = styled.div`
  width: 100%;
  ${({ sibling, isMobile }) => _getCardPadding(sibling, isMobile)};
`;

const AccessWrapper = styled(Card)`
  box-sizing: border-box;
  max-width: 688px;
  flex-direction: row;
  justify-content: space-between;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
`;

/**
 * Account settings screen. User can edit personal information or change password
 */
const AccountSettingsScreen = (props: ManagedProps<Props>): React$Node => {
  const { location: { state } = {}, viewport, history } = props;
  const location = useLocation();

  const dispatch = useDispatch();

  const { notificationDispatch } = useNotification();

  /**
   * TODO: might have to review this further
   */
  const resetRouteState = (): void => {
    if (state?.passwordUpdated) history.replace();
    if (state?.userInfoUpdated) history.replace();
  };

  const getStatusFromChanges = (): void => {
    if (state?.userInfoUpdated) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Your personal information has been updated',
        },
      });
    }
    if (state?.passwordUpdated) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Your password has been updated',
        },
      });
    }
    resetRouteState();
  };

  const userService = useSelector(({ user }) => user?.GET_USER_INFO_API ?? {}, shallowEqual);

  useEffect(() => {
    dispatch({
      type: ActionTypes.user.getInfo.START,
    });
    getStatusFromChanges();
    if (userService?.error?.message && !userService?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: userService?.error?.message,
        },
      });
    }
  }, []);

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_personal_information_settings,
      ...(location?.state?.from && {
        properties: {
          referrer: location?.state?.from,
        },
      }),
    });
  }, []);

  const goTo = (path: string): void => {
    history.push(`/${path}`);
  };

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  return (
    <>
      <Helmet>
        <title>Account Settings | ZIYNX</title>
      </Helmet>
      <ContentContainer>
        <PageWrapper isMobile={isMobile}>
          <StyledHeadingWrapper>
            <Heading large color={Colors.GrayVeryDark}>
              Settings
            </Heading>
          </StyledHeadingWrapper>
          <PageContentWrapper>
            {userService?.fetching && !userService?.lastName
              ? (
                <SpinnerWrapper>
                  <LoadingSpinner />
                </SpinnerWrapper>
              ) : (
                <>
                  <CardWrapper sibling="bottom">
                    <StyledHeadingWrapper small>
                      <Heading small color={Colors.GrayDark}>
                        Personal information
                      </Heading>
                    </StyledHeadingWrapper>
                    <Card
                      variant="info"
                      data={[
                        {
                          icon: 'personal',
                          label: 'First Name',
                          text: userService?.firstName ?? '.',
                        },
                        {
                          icon: 'personal',
                          label: 'Last Name',
                          text: userService?.lastName ?? '.',
                        },
                      ]}
                      rightAction={{ onClick: () => goTo('settings/personal-info/edit') }}
                    />
                  </CardWrapper>
                  <CardWrapper sibling="top" isMobile={isMobile} lastCard>
                    {!isMobile && (
                      <StyledHeadingWrapper small>
                        <Heading small color={Colors.GrayDark}>
                          Access
                        </Heading>
                      </StyledHeadingWrapper>
                    )}
                    <AccessWrapper isMobile={isMobile} width="initial">
                      <ListItem
                        variant="icon"
                        icon={{ name: 'key' }}
                        label={{
                          text: 'Password',
                          position: 'top',
                        }}
                        text="••••••••••••"
                      />
                      <Link
                        variant="text"
                        large
                        color={Colors.PrimaryBlue}
                        onClick={() => goTo('settings/change-password')}
                      >
                        Change
                      </Link>
                    </AccessWrapper>
                  </CardWrapper>
                </>
              )}
          </PageContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(AccountSettingsScreen): React$ComponentType<ManagedProps<Props>>);
